import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useVirtualizer} from "@tanstack/react-virtual";
import type {ForwardedRef, ReactElement, Ref} from "react";
import {forwardRef, useImperativeHandle, useRef} from "react";
import {useTranslation} from "react-i18next";
import type {PickerItem} from "../pickerItem";
import OverlayScrollArea from "./OverlayScrollArea";
import PickerRow from "./PickerRow";
import type {PickerEntry} from "./pickerEntries";

/** The list never grows past this; fewer rows and the panel shrinks to fit them. */
export const PICKER_VIEWPORT_HEIGHT = 300;

const OVERSCAN = 8;

/*
    First guesses only: every entry is measured once it renders. Row = 4px padding twice around the
    28px ⓘ button; heading = one 14px body2 line plus its padding, 8px more when it is not the first.
 */
const ROW_ESTIMATE = 36;
const FIRST_HEADING_ESTIMATE = 24;
const HEADING_ESTIMATE = 32;

/** The extra top padding on every heading but the first: the thesis' `mt: 1` between groups. */
const HEADING_GAP = 8;

export interface PickerListHandle {
    /** Brings one entry into view at the nearest edge; does nothing when it is already in view. */
    scrollToIndex: (entryIndex: number) => void;
}

export interface PickerListProps<I extends PickerItem = PickerItem> {
    entries: readonly PickerEntry<I>[];
    query: string;
    /** The selected row, among rows. */
    selectedIndex: number;
    listboxId: string;
    optionId: (item: I) => string;
    /** Key of the item whose info card is open. */
    infoKey: string | null;
    emptyLabel?: string;
    width: number;
    onSelect: (item: I) => void;
    onPointerSelect: (index: number, x: number, y: number) => void;
    onOpenInfo: (anchor: HTMLElement, item: I, pinned: boolean) => void;
    onCloseInfoSoon: () => void;
    onScroll: () => void;
}

function PickerListInner<I extends PickerItem>(
    {
        entries,
        query,
        selectedIndex,
        listboxId,
        optionId,
        infoKey,
        emptyLabel,
        width,
        onSelect,
        onPointerSelect,
        onOpenInfo,
        onCloseInfoSoon,
        onScroll,
    }: PickerListProps<I>,
    ref: ForwardedRef<PickerListHandle>,
) {
    const {t} = useTranslation();
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const virtualizer = useVirtualizer({
        count: entries.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: index => entries[index]?.kind === "heading"
            ? index === 0 ? FIRST_HEADING_ESTIMATE : HEADING_ESTIMATE
            : ROW_ESTIMATE,
        getItemKey: index => entries[index]?.key ?? index,
        overscan: OVERSCAN,
        initialRect: {width, height: PICKER_VIEWPORT_HEIGHT},
    });

    /*
        "auto" alignment, decided against the element's live scrollTop rather than the virtualizer's
        cached offset: navigation reveals a heading and then its row in the same tick, and the cache
        only catches up on the next scroll event, so the second call would undo the first.
     */
    useImperativeHandle(ref, () => ({
        scrollToIndex: entryIndex => {
            const element = scrollRef.current;
            const entry = virtualizer.measurementsCache[entryIndex];
            if (!element || !entry) return;

            // Revealed at the top edge, a heading's gap stays above the edge as the thesis' margin did.
            const gap = entryIndex > 0 && entries[entryIndex]?.kind === "heading" ? HEADING_GAP : 0;

            if (entry.start + gap < element.scrollTop) {
                virtualizer.scrollToOffset(entry.start + gap, {align: "start"});
            } else if (entry.end > element.scrollTop + element.clientHeight) {
                virtualizer.scrollToIndex(entryIndex, {align: "end"});
            }
        },
    }), [virtualizer, entries]);

    const empty = entries.length === 0;

    return (
        <OverlayScrollArea ref={scrollRef} maxHeight={PICKER_VIEWPORT_HEIGHT} onScroll={onScroll}>
            <Box
                component="ul"
                id={listboxId}
                role="listbox"
                sx={{
                    listStyle: "none",
                    m: 0,
                    p: 0,
                    ...(empty ? {} : {position: "relative", height: virtualizer.getTotalSize()}),
                }}
            >
                {virtualizer.getVirtualItems().map(virtual => {
                    const entry = entries[virtual.index];
                    if (!entry) return null;

                    const style = {
                        position: "absolute" as const,
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtual.start}px)`,
                    };

                    if (entry.kind === "heading") {
                        return (
                            <Typography
                                key={virtual.key}
                                ref={virtualizer.measureElement}
                                data-index={virtual.index}
                                component="li"
                                role="presentation"
                                variant="body2"
                                style={style}
                                sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    pl: "21px",
                                    pr: 2,
                                    // The thesis' `mt: 1` between groups, as padding: margins are not measured.
                                    pt: virtual.index > 0 ? 2 : 1,
                                    pb: 0.25,
                                }}
                            >
                                {entry.label}
                            </Typography>
                        );
                    }

                    return (
                        <PickerRow
                            key={virtual.key}
                            ref={virtualizer.measureElement}
                            item={entry.item}
                            index={entry.row}
                            entryIndex={virtual.index}
                            id={optionId(entry.item)}
                            query={query}
                            selected={entry.row === selectedIndex}
                            infoOpen={infoKey === entry.item.key}
                            style={style}
                            onPointerSelect={onPointerSelect}
                            onSelect={onSelect}
                            onOpenInfo={onOpenInfo}
                            onCloseInfoSoon={onCloseInfoSoon}
                        />
                    );
                })}

                {empty && (
                    <Box component="li" role="presentation" sx={{px: 2, py: 3, textAlign: "center"}}>
                        <Typography variant="body2" color="text.secondary">
                            {emptyLabel ?? (query.trim()
                                ? t("No variable matches \"{{query}}\"", {query: query.trim()})
                                : t("No variables"))}
                        </Typography>
                    </Box>
                )}
            </Box>
        </OverlayScrollArea>
    );
}

/** The virtualized list: group headings and rows as one run of measured entries, or the empty state. */
export const PickerList = forwardRef(PickerListInner) as <I extends PickerItem = PickerItem>(
    props: PickerListProps<I> & {ref?: Ref<PickerListHandle>},
) => ReactElement | null;

export default PickerList;
